/**
 * Add Account Sensors Dialog
 * Search and select sensors from the job's account to assign.
 */

import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useSensorsByAccount, useUpdateInstallationJob } from '../hooks/useInstallationJobs';
import type { InstallationJob, SensorInfo } from '../types';

interface AddAccountSensorsDialogProps {
  job: InstallationJob;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddAccountSensorsDialog({ job, open, onOpenChange }: AddAccountSensorsDialogProps) {
  const { data: accountSensors, isLoading } = useSensorsByAccount(job.accountId);
  const updateJob = useUpdateInstallationJob();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const assignedIds = useMemo(
    () => new Set(job.sensors.map((s) => s.mongoSensorId)),
    [job.sensors],
  );

  const filteredSensors = useMemo(() => {
    if (!accountSensors) return [];
    const q = search.toLowerCase();
    return accountSensors.filter((s) => {
      if (assignedIds.has(s._id)) return false; // hide already assigned
      if (!q) return true;
      return s.name.toLowerCase().includes(q) || s.location?.name?.toLowerCase().includes(q);
    });
  }, [accountSensors, search, assignedIds]);

  const toggleSensor = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;

    const newSensors: SensorInfo[] = [];
    for (const id of selected) {
      const sensor = accountSensors?.find((s) => s._id === id);
      if (sensor) {
        newSensors.push({
          mongoSensorId: sensor._id,
          sensorName: sensor.name,
          flags: [],
          installed: false,
        });
      }
    }

    try {
      await updateJob.mutateAsync({
        id: job.id,
        input: { sensors: [...job.sensors, ...newSensors] },
      });
      toast.success(`Added ${newSensors.length} sensor(s)`);
      setSelected(new Set());
      setSearch('');
      onOpenChange(false);
    } catch {
      toast.error('Failed to add sensors');
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelected(new Set());
      setSearch('');
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Sensors</DialogTitle>
          <DialogDescription>
            Select sensors from this account to add to the job.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sensors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-72 overflow-y-auto space-y-1">
          {isLoading ? (
            <div className="space-y-2 py-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 w-full rounded bg-muted animate-pulse" />
              ))}
            </div>
          ) : filteredSensors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {search ? 'No sensors match your search' : 'All sensors are already assigned'}
            </p>
          ) : (
            filteredSensors.map((sensor) => (
              <label
                key={sensor._id}
                className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
              >
                <Checkbox
                  checked={selected.has(sensor._id)}
                  onCheckedChange={() => toggleSensor(sensor._id)}
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{sensor.name}</span>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {sensor.location?.name && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {sensor.location.name}
                      </span>
                    )}
                    {sensor.stream?.name && (
                      <span>{sensor.stream.name}</span>
                    )}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selected.size === 0 || updateJob.isPending}
          >
            {updateJob.isPending ? 'Adding...' : `Add ${selected.size || ''} Sensor${selected.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
